import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import InputLabel from '@material-ui/core/InputLabel';
import { riskColors } from 'utils/constants/risks';
import MapWithRegions from 'components/Map/MapOptions/MapWithRegions';
import styles from './index.module.scss';
import {
  DEFAULT_ZOOM_LEVEL,
  DISTANCE,
  DrawingOptions,
  LOCATION_MODE,
} from 'utils/constants/Map';
import DrawingMenuOptions from 'components/Map/MapOptions/DrawingMenu';
import Tooltip, { InfoPropTypes } from 'components/Tooltip';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { getRegionCentreOfGravity } from 'reducers/region';

const PolygonCreator = ({
  center,
  label,
  value = null,
  markers,
  onChange,
  required,
  eventType,
  error,
  setSecondaryValue,
  info,
  displaySites,
}) => {
  const { t } = useTranslation();
  const [drawingStyle, setDrawingStyle] = useState(DrawingOptions.polygon);
  const regionCenter = useSelector(state => getRegionCentreOfGravity(state));
  const updateEventType = useCallback(() => {
    onChange(value.map(item => ({ ...item, color: riskColors[eventType] })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventType]);
  const onMapClick = (evt, map) => {
    const lat = evt.latLng.lat();
    const lng = evt.latLng.lng();
    if (drawingStyle === DrawingOptions.rectangle) {
      const number = DISTANCE[map.getZoom()] || DISTANCE[DEFAULT_ZOOM_LEVEL];

      const northEast = {
        lat: lat + number,
        lng: lng - number,
      };
      const northWest = {
        lat: lat + number,
        lng: lng + number,
      };

      const southEast = {
        lat: lat - number,
        lng: lng - number,
      };
      const southWest = {
        lat: lat - number,
        lng: lng + number,
      };

      onChange([
        {
          editable: true,
          draggable: true,
          isFilled: true,
          color: riskColors[eventType],
          paths: [northEast, northWest, southWest, southEast],
        },
      ]);
      setSecondaryValue([northEast, northWest, southWest, southEast]);
    } else if (value.length > 0) {
      const [valueClone] = value;
      onChange([
        {
          editable: true,
          draggable: false,
          isFilled: true,
          color: riskColors[eventType],
          paths: valueClone.paths.concat({ lat, lng }),
        },
      ]);
      setSecondaryValue(valueClone.paths.concat({ lat, lng }));
    } else {
      onChange([
        {
          editable: true,
          draggable: false,
          isFilled: true,
          color: riskColors[eventType],
          paths: [{ lat, lng }],
        },
      ]);
      setSecondaryValue([{ lat, lng }]);
    }
  };

  const clear = () => {
    setDrawingStyle(DrawingOptions.polygon);
    onChange();
    setSecondaryValue();
  };

  const setValues = vals => {
    onChange(vals);
  };

  function updateStyle(style) {
    setDrawingStyle(style);
    onChange([]);
    setSecondaryValue();
  }

  const menuOptions = [
    {
      type: DrawingOptions.polygon,
      onClick: () => updateStyle(DrawingOptions.polygon),
    },
    {
      type: DrawingOptions.rectangle,
      onClick: () => updateStyle(DrawingOptions.rectangle),
    },
  ];

  useEffect(() => {
    updateEventType();
  }, [updateEventType]);

  const textErrorOrBool = typeof error === 'string' ? t(error) : error;

  return (
    <div className={styles.panelContainer}>
      <div className={styles.header}>
        <InputLabel shrink className={styles.label} required={required}>
          {t(label)}
          {info && <Tooltip text={info} />}
        </InputLabel>
        <span className={styles.error}>{textErrorOrBool}</span>
      </div>

      <div className={styles.mapContainer}>
        <MapWithRegions
          markers={markers}
          center={center || regionCenter}
          onMapClick={onMapClick}
          secondaryPolygon={value}
          secondaryPolygonMode={LOCATION_MODE.HIGH_DETAIL}
          setValues={setValues}
          clear={clear}
          isZoomEnabled={false}
          drawingStyle={drawingStyle}
          displaySites={displaySites}
          mapInForm={true}
          customMapControl={
            <DrawingMenuOptions
              options={menuOptions}
              onClear={clear}
              defaultDrawingTool={DrawingOptions.polygon}
            />
          }
        />
      </div>
    </div>
  );
};

PolygonCreator.propTypes = {
  label: PropTypes.string,
  center: PropTypes.object,
  markers: PropTypes.array,
  value: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  onChange: PropTypes.func,
  required: PropTypes.bool,
  eventType: PropTypes.string,
  setSecondaryValue: PropTypes.func,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  info: InfoPropTypes,
  displaySites: PropTypes.bool,
  panTo: PropTypes.func,
  requirePan: PropTypes.bool,
};

PolygonCreator.defaultProps = {
  value: [],
};

export default PolygonCreator;
